import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { news } from "@/mocks/news";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import HeroCarousel from "./components/HeroCarousel";

export default function Home() {
  useDocumentMeta(
    "欧美三级电影资源与资讯平台 - 欧美三级影视大全",
    "欧美三级为您提供丰富的欧美三级电影资源和最新资讯，涵盖剧情介绍、影片分类和观看指南，助您轻松了解欧美三级影视内容。",
    "欧美三级,欧美三级电影,三级电影资源,欧美影视,三级电影介绍,欧美三级影片,电影资讯,影视大全"
  );

  const featured = movies.filter((m) => m.featured).slice(0, 4);
  const latest = [...movies].sort((a, b) => b.year - a.year || b.rating - a.rating).slice(0, 10);
  const hotNews = [...news].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 6);
  const featuredNews = hotNews[0];

  return (
    <div className="mx-auto max-w-[1240px] px-4 py-6 md:px-6 md:py-8">
      <HeroCarousel movies={featured} />

      {/* 分类快捷入口 */}
      <section className="mt-10 md:mt-14">
        <SectionHeading
          title="影片分类"
          subtitle="按类型快速找到你感兴趣的欧美影视作品"
          moreTo="/category"
          moreLabel="全部分类"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category?genre=${encodeURIComponent(cat.slug)}`}
              className="group flex cursor-pointer flex-col gap-3 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 hover:bg-primary-50/60"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700 transition-colors group-hover:bg-accent-500 group-hover:text-primary-950">
                <i className={`${cat.icon} text-2xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">{cat.name}</h3>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-secondary-500">{cat.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新电影 */}
      <section className="mt-10 md:mt-14">
        <SectionHeading
          title="最新电影"
          subtitle="按上映时间为你推荐近期更新的影片"
          moreTo="/category"
          moreLabel="查看更多"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* 热门资讯 */}
      <section className="mt-10 md:mt-14">
        <SectionHeading
          title="热门资讯"
          subtitle="影视动态、影展资讯与深度专题"
          moreTo="/news"
          moreLabel="全部资讯"
        />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="divide-y divide-background-200/80 overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
              {hotNews.slice(1).map((item) => (
                <Link
                  key={item.id}
                  to={`/news-detail/${item.id}`}
                  className="group flex cursor-pointer flex-col gap-2 p-4 transition-colors hover:bg-background-100 md:flex-row md:items-center md:gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800">
                        {item.category}
                      </span>
                      <span className="text-xs text-secondary-500">{item.date}</span>
                    </div>
                    <h3 className="mt-2 line-clamp-1 font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                      {item.title}
                    </h3>
                  </div>
                  <i className="ri-arrow-right-s-line hidden text-xl text-secondary-400 transition-transform group-hover:translate-x-1 group-hover:text-accent-600 md:block"></i>
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <Link
              to={`/news-detail/${featuredNews.id}`}
              className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200/80 bg-background-50"
            >
              <div className="h-44 w-full overflow-hidden bg-background-200 md:h-48">
                <img
                  src={featuredNews.cover}
                  alt={featuredNews.title}
                  title={`${featuredNews.title} - 影视资讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className="w-fit rounded-md bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-700">
                  专题推荐
                </span>
                <h3 className="mt-2 line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                  {featuredNews.title}
                </h3>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-secondary-500">{featuredNews.summary}</p>
                <span className="mt-auto pt-3 text-xs text-secondary-500">{featuredNews.date}</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 网站简述 */}
      <section className="mt-10 md:mt-14">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200/80 bg-background-100 p-6 md:grid-cols-2 md:p-10">
          <div>
            <span className="text-xs font-semibold tracking-widest text-accent-600">ABOUT US</span>
            <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              关于欧美三级影视平台
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              欧美三级专注欧美限制级剧情与文艺影视作品，聚合影片分类、剧情介绍、影片标签与最新影视资讯，帮助你高效查找与了解感兴趣的内容。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">
              我们坚持内容的专业与丰富，所有影片资料结构清晰、信息完整。本平台为资讯展示性质，不提供任何在线播放与下载服务。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
              >
                开始浏览
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                了解更多
              </Link>
            </div>
          </div>
          <div className="h-56 w-full overflow-hidden rounded-lg md:h-72">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20editorial%20scene%20of%20a%20dark%20elegant%20home%20cinema%20with%20warm%20amber%20accent%20lighting%20and%20deep%20navy%20walls%2C%20refined%20artistic%20mood%2C%20no%20text&width=1200&height=800&seq=home-about-01&orientation=landscape"
              alt="欧美三级影视平台介绍"
              title="欧美三级影视资源与资讯"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>
    </div>
  );
}